import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from 'services/settings/SettingsContext';
import { useModal } from 'components/modals/ModalContext';
import { renderDate } from 'components/lists/functions/renderListColumn';
import PurchaseModal from './PurchaseModal';
import { PurchaseType } from './PurchaseTypes';
import '../../style/scss/modals.scss';

const PurchaseSummaryCard: React.FC<{
    purchases: PurchaseType[];
}> = ({
    purchases,
}) => {
    const { t } = useTranslation();
    const { userLocale } = useSettings();
    const { initializeModal } = useModal();

    // Open the purchase modal of the clicked purchase
    const openPurchase = (purchaseId: number | null) => {
        if (!purchaseId) return;

        initializeModal(
            <PurchaseModal itemId={purchaseId} />, {
                itemId: purchaseId,
                modalSize: 'large'
            }
        );
    };

    // Render the title of a purchase card item
    const renderPurchaseTitle = (purchase: PurchaseType) => {
        // Render the number as a draft number for draft purchases
        const number = purchase.status === 'draft'
            ? t('purchase.general.purchase_draft_label', { number: purchase.number })
            : purchase.number;

        // Combine the supplier name with the number when a supplier is set
        return purchase.supplier_name ? `${purchase.supplier_name} · ${number}` : number;
    };

    return (
        <div className='summary-object-card'>
            <h6>
                {t('general.object_name_capitalize', {
                    object_name: t(`purchase.general.object_name.${purchases.length > 1 ? 'plural' : 'singular'}`)
                })}
            </h6>
            {purchases.map((purchase) => (
                <div className='summary-object-card-item'
                    key={purchase.id}
                    onClick={() => openPurchase(purchase.id)}>
                    <div className='item-text'>
                        <div className='item-title'>
                            {renderPurchaseTitle(purchase)}
                        </div>
                        <div className='item-subtitle'>
                            {t(`purchase.status.${purchase.status}`)}
                        </div>
                    </div>
                    {purchase.status !== 'delivered' && purchase.expected_delivery_date && (
                        <div className='item-meta item-meta-stacked'>
                            <div className='meta-value'>
                                {renderDate(purchase.expected_delivery_date, userLocale)}
                            </div>
                            <div className='meta-label'>
                                {t('purchase.general.expected_label').toLowerCase()}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

export default PurchaseSummaryCard;
